// The brawl as one group chat: the three models and you in a single thread, the way
// a messenger shows a group. The models' turns are bubbles in their seat colours,
// yours sit on the right, and the rest of the brawl (rounds, changes of mind, the
// judge, web searches) are centred lines. Each round's vote is a poll, and the final
// answer is pinned at the top once it lands.
//
// src/app.js drives it alongside the three columns; the Chat / Columns switch only
// changes which one shows.
import { IDS } from "../lib/protocol.js";
import { escapeHtml } from "./body.js";

export function createThread(root) {
  const msgs = root.querySelector(".chat-msgs");
  const sub = root.querySelector(".chat-sub");
  const pin = root.querySelector(".chat-pin");
  let names = {};
  const typing = new Set();

  // Keeps the thread at its newest message, unless the reader scrolled up.
  const follow = (update) => {
    const atBottom = msgs.scrollHeight - msgs.scrollTop - msgs.clientHeight < 60;
    update();
    if (atBottom) msgs.scrollTop = msgs.scrollHeight;
  };
  const add = (el) => {
    follow(() => msgs.append(el));
    return el;
  };
  const div = (cls, html = "") => {
    const el = document.createElement("div");
    el.className = cls;
    el.innerHTML = html;
    return el;
  };

  // The header's second line: who's in the group, or who's typing right now.
  const refreshSub = () => {
    const who = IDS.filter((id) => typing.has(id)).map((id) => names[id]);
    sub.classList.toggle("typing", who.length > 0);
    sub.textContent = who.length
      ? `${who.length > 1 ? `${who.slice(0, -1).join(", ")} and ${who.at(-1)} are` : `${who[0]} is`} typing…`
      : `${IDS.map((id) => names[id]).join(", ")}, you`;
  };

  const polls = new Map();
  let sample = false; // showing a sample brawl (src/sample.js), not the visitor's own

  return {
    reset() {
      sample = false;
      root.classList.remove("sample");
      msgs.innerHTML = "";
      polls.clear();
      typing.clear();
      this.unpin();
      refreshSub();
    },

    sample(on) {
      sample = on;
      root.classList.toggle("sample", on);
      msgs.scrollTop = 0;
    },
    isSample: () => sample,

    members(seatNames) {
      names = seatNames;
      root.querySelectorAll(".chat-avatars [data-seat]").forEach((a) => (a.title = names[a.dataset.seat]));
      refreshSub();
    },

    typing(id, on) {
      if (on) typing.add(id);
      else typing.delete(id);
      refreshSub();
    },

    // Your own message: the question or a moderator note.
    you(text, label = "") {
      const el = div("bubble you", `${label ? `<div class="who">${escapeHtml(label)}</div>` : ""}<div class="text"></div>`);
      el.querySelector(".text").textContent = text;
      return add(el);
    },

    // A centred line: "Round 2", "Sol 6.1 now backs Opus 5.5", a web search.
    // `html` is trusted markup from the app (a ticking "Searching…"), never model text.
    system(text, { kind = "", html = null } = {}) {
      const el = div(`sys ${kind}`.trim(), "<span></span>");
      if (html != null) el.firstElementChild.innerHTML = html;
      else el.firstElementChild.textContent = text;
      return add(el);
    },

    judge(text) {
      const el = div("bubble judge", `<div class="who">Judge</div><div class="text"></div>`);
      el.querySelector(".text").textContent = text;
      return add(el);
    },

    // A model's turn as a bubble. `paint(html)` replaces its text (already escaped by
    // renderBody), `note(text)` adds to its label (the cost), `remove()` takes it back
    // (a retried turn). `confidence(n)` shows how sure the model said it is, top right;
    // `vote(voter)` hangs a star in the voter's colour off the bottom of the bubble,
    // like a reaction: hollow when a model backs itself.
    turn(id, { label, final = false }) {
      const el = div(`bubble model${final ? " final" : ""}`, `<div class="who"><span class="name"></span> <span class="meta"></span><span class="conf"></span></div><div class="text msg-body"></div>`);
      el.dataset.seat = id;
      el.style.setProperty("--c", `var(--${id})`);
      el.querySelector(".name").textContent = names[id];
      el.querySelector(".meta").textContent = label;
      add(el);
      const text = el.querySelector(".text");
      return {
        el,
        paint: (html) => follow(() => (text.innerHTML = html)),
        plain: (s) => follow(() => (text.textContent = s)),
        note: (s) => (el.querySelector(".meta").textContent += ` ${s}`),
        state: (cls, on) => el.classList.toggle(cls, on),
        remove: () => el.remove(),
        confidence: (n) => {
          const c = el.querySelector(".conf");
          c.textContent = `${n}% sure`;
          c.title = `Confidence: how sure ${names[id]} says it is of its verdict`;
        },
        vote: (voter) => {
          let votes = el.querySelector(".votes");
          if (!votes) {
            votes = div("votes");
            votes.setAttribute("role", "img");
            el.append(votes);
            el.classList.add("voted");
          }
          const self = voter === id;
          const star = document.createElement("span");
          star.className = `star${self ? " self" : ""}`;
          star.dataset.seat = voter;
          star.style.setProperty("--v", `var(--${voter})`);
          star.textContent = self ? "☆" : "★";
          star.title = self ? `${names[voter]} backs its own verdict` : `${names[voter]} voted for this`;
          votes.append(star);
          const who = [...votes.children].map((s) => (s.dataset.seat === id ? `${names[id]} (itself)` : names[s.dataset.seat]));
          votes.setAttribute("aria-label", `Votes: ${who.join(", ")}`);
          votes.title = `Voted for by ${who.join(", ")}`;
        },
      };
    },

    // A round's vote as a poll: one option per model, its bar the share of the votes
    // behind it, with who voted for it. Posted when the round's votes are in, and
    // updated in place as its result changes (the judge checking, then its call).
    //   { title, options: [{ id, name, votes, voters }], total, result, kind }
    poll(key, { title, options, total, result, kind }) {
      let el = polls.get(key);
      if (!el) {
        el = add(div("poll", `<div class="poll-title"></div><div class="poll-options"></div><div class="poll-result"></div>`));
        polls.set(key, el);
      }
      el.querySelector(".poll-title").textContent = title;
      const list = el.querySelector(".poll-options");
      list.innerHTML = "";
      for (const o of options) {
        const row = div("poll-option", `<div class="poll-line"><span class="name"></span><span class="count"></span></div><div class="poll-bar"><div></div></div><div class="voters"></div>`);
        row.style.setProperty("--c", `var(--${o.id})`);
        row.querySelector(".name").textContent = o.name;
        row.querySelector(".count").textContent = `${o.votes} vote${o.votes === 1 ? "" : "s"}`;
        row.querySelector(".poll-bar div").style.width = `${total ? (o.votes / total) * 100 : 0}%`;
        row.querySelector(".voters").textContent = o.voters.length ? o.voters.join(", ") : "";
        list.append(row);
      }
      const res = el.querySelector(".poll-result");
      res.textContent = result;
      res.dataset.kind = kind;
      return el;
    },

    // The final answer, pinned at the top of the thread: a click goes back to it.
    // `share: false` hides Share (a sample brawl isn't the visitor's to share).
    pinAnswer(bubble, label, { share = true } = {}) {
      pin.hidden = false;
      pin.querySelector(".share").hidden = !share;
      pin.querySelector(".pin-label").textContent = label;
      pin.querySelector(".pin-go").onclick = () => bubble.scrollIntoView({ behavior: "smooth", block: "nearest" });
    },
    unpin() {
      pin.hidden = true;
      pin.querySelector(".pin-go").onclick = null;
    },
  };
}
